const getStringFromUnknown = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidates = [
    'summary',
    'ai_analysis',
    'aiAnalysis',
    'analysis',
    'root_cause',
    'rootCause',
    'root-cause',
    'description',
    'detailed_description',
    'highLevelIssues',
    'message',
    'text',
    'data',
  ] as const;

  for (const key of candidates) {
    const extracted = getStringFromUnknown((value as Record<string, unknown>)[key]);
    if (extracted) return extracted;
  }

  return undefined;
};

const hasDealId = (value: unknown, dealId: string): boolean => {
  if (!value || typeof value !== 'object') return false;

  const object = value as Record<string, unknown>;
  const candidate = object.deal_id ?? object.dealId ?? object.id;
  if (candidate !== undefined && String(candidate) === dealId) return true;

  return hasDealId(object.data, dealId);
};

const findDealDetails = (exchangeJson: unknown, dealId: string): unknown => {
  if (!exchangeJson) return undefined;
  if (Array.isArray(exchangeJson)) return exchangeJson.find(item => hasDealId(item, dealId)) ?? exchangeJson[0];

  if (typeof exchangeJson === 'object') {
    const object = exchangeJson as Record<string, unknown>;
    const direct = object[dealId];
    if (direct !== undefined) return direct;

    const collection = object.root_causes ?? object.results ?? object.data ?? object.items;
    if (Array.isArray(collection)) return collection.find(item => hasDealId(item, dealId)) ?? collection[0];

    return Object.values(object)[0];
  }

  return undefined;
};

const hasUnsuccessfulStatus = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;

  const object = value as Record<string, unknown>;
  if (!Object.hasOwn(object, 'status')) return false;

  return object.status !== 'SUCCESS';
};

const getHealthScoreFromUnknown = (value: unknown): number | undefined => {
  if (!value || typeof value !== 'object') return undefined;

  const object = value as Record<string, unknown>;
  const score = object.healthScore ?? object.health_score;
  if (typeof score === 'number' && Number.isFinite(score)) return score;

  return getHealthScoreFromUnknown(object.data);
};

export const extractDealHealthScoreRootCauseDetails = (
  exchangeJson: unknown,
  dealId: string
): { aiAnalysis?: string; currentScore?: number } => {
  const details = findDealDetails(exchangeJson, dealId);

  if (hasUnsuccessfulStatus(details)) {
    return {};
  }

  return {
    aiAnalysis: getStringFromUnknown(details),
    currentScore: getHealthScoreFromUnknown(details),
  };
};

export const extractAiAnalysisFromExchangeJson = (exchangeJson: unknown, dealId: string): string | undefined => {
  return extractDealHealthScoreRootCauseDetails(exchangeJson, dealId).aiAnalysis;
};
